import React from 'react';
import { ActivityRendererProps } from './types';

const ReadActivity: React.FC<ActivityRendererProps> = ({ activity }) => {
  const content = activity.content;

  // Render a generic list item with flexible fields
  const renderListItem = (item: any, index: number, primaryKey: string, secondaryKey: string, additionalKeys: string[] = []) => (
    <div key={index} className="content-list-item">
      <div className="item-primary">
        <strong>{item[primaryKey]}</strong>
      </div>
      <div className="item-secondary">{item[secondaryKey]}</div>
      {additionalKeys.map((key) => 
        item[key] && (
          <div key={key} className={`item-additional item-${key}`}>
            {typeof item[key] === 'string' ? (
              <em>{item[key]}</em>
            ) : (
              item[key]
            )}
          </div>
        )
      )}
    </div>
  );

  return (
    <div className="read-activity">
      <div className="read-content">
        {content?.text && <p className="main-text">{content.text}</p>}
        
        {/* Key Terms */}
        {content?.keyTerms && (
          <div className="content-section key-terms-list">
            <h3>Key Terms:</h3>
            {content.keyTerms.map((item: any, index: number) =>
              renderListItem(item, index, 'term', 'definition')
            )}
          </div>
        )}
        
        {/* Examples */}
        {content?.examples && (
          <div className="content-section examples-list">
            <h3>Examples:</h3>
            {content.examples.map((example: any, index: number) =>
              renderListItem(example, index, 'title', 'description', ['lesson', 'principle', 'insight'])
            )}
          </div>
        )}
        
        {/* Strategies */}
        {content?.strategies && (
          <div className="content-section strategies-list">
            <h3>Key Strategies:</h3>
            {content.strategies.map((strategy: any, index: number) =>
              renderListItem(strategy, index, 'strategy', 'description', ['application'])
            )}
          </div>
        )}
        
        {/* Vocabulary (Chinese learning specific) */}
        {content?.vocabulary && (
          <div className="content-section vocabulary-list">
            <h3>New Vocabulary:</h3>
            {content.vocabulary.map((item: any, index: number) => (
              <div key={index} className="vocabulary-item">
                <div className="vocabulary-word">
                  <span className="chinese">{item.word}</span>
                  <span className="pinyin">{item.pinyin}</span>
                </div>
                <div className="vocabulary-meaning">{item.meaning}</div>
              </div>
            ))}
          </div>
        )}
        
        {/* Manifestations */}
        {content?.manifestations && (
          <div className="content-section manifestations-list">
            <h3>How It Manifests:</h3>
            {content.manifestations.map((item: any, index: number) =>
              renderListItem(item, index, 'type', 'description', ['example'])
            )}
          </div>
        )}
        
        {/* Benefits */}
        {content?.benefits && (
          <div className="content-section benefits-list">
            <h3>Benefits:</h3>
            {content.benefits.map((item: any, index: number) =>
              renderListItem(item, index, 'benefit', 'description', ['example'])
            )}
          </div>
        )}
        
        {/* Steps */}
        {content?.steps && (
          <div className="content-section steps-list">
            <h3>How to Apply:</h3>
            {content.steps.map((item: any, index: number) =>
              renderListItem(item, index, 'step', 'description', ['example'])
            )}
          </div>
        )}
        
        {/* Mistakes */}
        {content?.mistakes && (
          <div className="content-section mistakes-list">
            <h3>Common Mistakes:</h3>
            {content.mistakes.map((item: any, index: number) => (
              <div key={index} className="content-list-item">
                <div className="item-primary">
                  <strong>{item.mistake}</strong>
                </div>
                <div className="item-secondary">{item.description}</div>
                {item.example && (
                  <div className="item-additional item-example">
                    <em>Example: {item.example}</em>
                  </div>
                )}
                {item.correction && (
                  <div className="item-additional item-correction">
                    <strong>→ Correction:</strong> {item.correction}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Dangers (simple list) */}
        {content?.dangers && (
          <div className="content-section dangers-list">
            <h3>Potential Dangers:</h3>
            <ul>
              {content.dangers.map((danger: string, index: number) => (
                <li key={index}>{danger}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Applications (simple list) */}
        {content?.applications && (
          <div className="content-section applications-list">
            <h3>Applications:</h3>
            <ul>
              {content.applications.map((app: string, index: number) => (
                <li key={index}>{app}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReadActivity;

